import React, { useState } from 'react';
import { Query, useMutation } from 'react-apollo';
import { Link, Route, Switch } from 'react-router-dom';
import Helmet from 'react-helmet/es/Helmet';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import MaterialTable from 'material-table';
import BonusIcon from '@material-ui/icons/MonetizationOn';
import Spin from 'components/common/Spin';
import RadioGroup from 'components/form/Radio';
import {
  GET_COACHES,
  GET_PAYMENT_ITEMS,
  UPDATE_PAYMENT_ITEM,
  GET_ALL_COACHES,
} from 'graphql/payments';
import { INSERT_NOTIFICATION } from 'graphql/notification';
import { getInitials, getFullName } from 'utils';
import AccountSettings from './AccountSettings';
import Categories from './Categories';
import { useSelector } from 'react-redux';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import BonusPayment from './BonusPayment';

const TimeLogs = () => {
  const [updateItem] = useMutation(UPDATE_PAYMENT_ITEM);
  const [insertNotification] = useMutation(INSERT_NOTIFICATION);
  const user = useSelector((s) => s.user.user);

  const onApprove = async (row) => {
    if (window.confirm('Confirm approve?')) {
      await Promise.all(
        row.map((r) => {
          updateItem({
            variables: {
              id: r.id,
              data: {
                status: 'approved',
                approved: true,
                approved_by: user.id,
              },
            },
          });
        }),
      );
    }
  };

  const onReject = async (row) => {
    if (window.confirm('Confirm reject?')) {
      await Promise.all(
        row.map((r) => {
          updateItem({
            variables: {
              id: r.id,
              data: {
                status: 'rejected',
                approved: false,
              },
            },
          });
          insertNotification({
            variables: {
              data: {
                actor_id: r.user_id,
                message: `Your time log entry ${r.payment_category.label} was rejected`,
                recipient_id: r.user_id,
                subject: 'payment_line_items',
                subject_id: r.id,
                topic: 'TIMELOG_REJECTED',
              },
            },
          });
        }),
      );
    }
  };

  return (
    <Query
      query={GET_PAYMENT_ITEMS}
      variables={{ status: 'submitted' }}
      fetchPolicy={'cache-and-network'}
    >
      {({ data, loading, error }) => {
        if (loading) return <Spin spinning />;
        if (error) return error.message;
        const { rows } = data;


        const columns = [
          {
            title: 'Coach',
            field: 'user',
            render: (row) => (
              <Link to={`/payments/accounts/${row.user_id}`}>
                {getFullName(row.user)}
              </Link>
            ),
            customFilterAndSearch: (term, row) => {
              return (row.user.user_first_name + row.user.user_last_name)
                .toLowerCase()
                .includes(term.toLowerCase());
            },
          },
          { title: 'Date', field: 'date', type: 'date', searchable: false },
          { title: 'Item', field: 'payment_category.label', searchable: false },
          {
            title: 'Student/Group',
            field: 'student.user_first_name',
            render: (row) => {
              if (row.group) return row.group.name;
              return getFullName(row.student);
            },
            searchable: false
          },
          {
            title: 'Time',
            field: 'duration',
            render: (row) => `${row.duration} min`,

          },
          {
            title: 'Amount',
            field: 'total',
            render: (row) => `$${row.total / 100}`,
            type: 'numeric',

          },
          { title: 'Status', field: 'status', searchable: false },
          { title: 'Comments', field: 'comments', searchable: false },
        ];

        return (
          <Box marginBottom={4}>
            <MaterialTable
              title="Pending time logs"
              options={{
                actionsColumnIndex: -1,
                pageSize: 10,
                selection: true,
              }}
              columns={columns}
              data={rows}
              localization={{
                body: {
                  emptyDataSourceMessage: 'No pending time logs to review',
                },
              }}
              actions={[
                (rowData) => ({
                  icon: 'check',
                  tooltip: 'Approve All Selected',
                  onClick: (event, rowData) => onApprove(rowData),
                }),
                (rowData) => ({
                  icon: 'block',
                  tooltip: 'Reject All Selected',
                  onClick: (event, rowData) => onReject(rowData),
                }),
              ]}
            />
          </Box>
        );
      }}
    </Query>
  );
};

const Coaches = () => {
  const [selected, setSelected] = useState();
  const [coach, setCoach] = useState('lead_coach');
  const [all, isAll] = useState(false);
  const handleRadiobtn = async (val) => {
    if (val === 'all') {
      isAll(true);
      setCoach(val);
    } else {
      await isAll(false);
      await setCoach(val);
    }
  };

  return (
    <>
      <Query
        query={!all ? GET_COACHES : GET_ALL_COACHES}
        variables={!all && { role: coach }}
        fetchPolicy={'cache-and-network'}
      >
        {({ data, loading, refetch }) => {
          const columns = [
            {
              title: 'Coach',
              render: (row) => (
                <Box display="flex" alignItems="center">
                  <Avatar src={row.user_pic}>
                    {getInitials(row.user_first_name, row.user_last_name)}
                  </Avatar>
                  &nbsp;
                  <Link to={`/payments/accounts/${row.id}`}>
                    {getFullName(row)}
                  </Link>
                </Box>
              ),
              customFilterAndSearch: (term, row) => {
                return (row.user_first_name + row.user_last_name)
                  .toLowerCase()
                  .includes(term.toLowerCase());
              },
            },
            {
              title: 'Categories',
              render: (row) =>
                row.user_payment_rates_aggregate.aggregate.count === 0
                  ? 'Not configured'
                  : row.user_payment_rates_aggregate.aggregate.count,
            },
            {
              title: 'Payout enabled',
              field: 'payment_account.payouts_enabled',
              render: (row) => {
                if (row.payment_account) {
                  return row.payment_account.payouts_enabled ? 'Yes' : 'No';
                }
                return 'Signup pending';
              },
            },
            {
              title: 'Next payout',
              render: (row) =>
                '$' +
                row.payment_line_items_aggregate.aggregate.sum.total / 100,
            },
          ];

          return (
            <Box>
              <MaterialTable
                title={
                  <Box marginTop={2}>
                    <RadioGroup
                      label="Select Coach"
                      style={{ flexDirection: 'row' }}
                      value={coach}
                      onChange={(e) => handleRadiobtn(e.target.value)}
                      options={[
                        { label: 'All', value: 'all' },
                        { label: 'College Coach', value: 'lead_coach' },
                        { label: 'Head Coach', value: 'head_coach' },
                        { label: 'Financial Aid Counselor', value: 'fa_coach' },
                      ]}
                    />
                  </Box>
                }
                options={{
                  pageSize: 10,
                  search: true,
                  actionsColumnIndex: -1,
                }}
                columns={columns}
                data={data?.users}
                isLoading={loading}
                actions={[
                  {
                    icon: 'settings',
                    onClick: (e, row) =>
                      setSelected({ coach: row, action: 'settings' }),
                  },
                  (row) =>
                    row.payment_account?.payouts_enabled && {
                      icon: BonusIcon,
                      title: 'Pay bonus',
                      onClick: (e, row) =>
                        setSelected({ coach: row, action: 'bonus' }),
                    },
                ]}
              />
              {selected && selected.action === 'settings' && (
                <AccountSettings
                  user={selected.coach}
                  onClose={() => setSelected()}
                  refetch={refetch}
                />
              )}
              {selected && selected.action === 'bonus' && (
                <BonusPayment
                  coach={selected.coach}
                  onClose={() => setSelected()}
                  refetch={refetch}
                />
              )}
            </Box>
          );
        }}
      </Query>
    </>
  );
};

const PaymentHome = ({ location }) => {
  const tabs = ['/payments', '/payments/accounts', '/payments/categories'];

  return (
    <div>
      <Helmet title="Payments" />
      <Box marginBottom={1}>
        <Tabs
          value={location.pathname}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            value={tabs[0]}
            label="Time logs"
            component={Link}
            to={tabs[0]}
          />
          <Tab value={tabs[1]} label="Coaches" component={Link} to={tabs[1]} />
          <Tab
            value={tabs[2]}
            label="Categories"
            component={Link}
            to={tabs[2]}
          />
        </Tabs>
      </Box>
      <Switch>
        <Route exact path="/payments" component={TimeLogs} />
        <Route exact path="/payments/accounts" component={Coaches} />
        <Route exact path="/payments/categories" component={Categories} />
      </Switch>
    </div>
  );
};

export default PaymentHome;
